import React from "react";
import PropTypes from "prop-types";
import "./Title.css";

/**
 * Composant `Title` pour afficher des titres stylisés et responsives.
 *
 * @param {React.ReactNode} children - Contenu texte ou JSX à afficher.
 * @param {string} level - Niveau de titre, valeurs possibles : 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'. Défaut : 'h1'.
 * @param {string} bold - Poids du texte, valeurs possibles : 'thin', 'extra-light', 'light', 'regular', 'medium', 'semi-bold', 'bold', 'extra-bold'. Défaut : 'bold'.
 * @param {number} line - Nombre de lignes maximum à afficher. Si défini à 1, applique un effet de text-overflow. Défaut : undefined.
 * @param {string} className - Classes CSS supplémentaires pour personnalisation.
 * @param {object} props - Propriétés additionnelles (comme onClick, id, etc.).
 * @returns {JSX.Element} Un élément HTML `<h1>` à `<h6>` stylisé.
 */
const Title = ({
  children,
  level = "h1",
  bold = "bold",
  line,
  className = "",
  ...props
}) => {
  const Tag = level; // Balise dynamique (h1, h2, etc.)

  return (
    <Tag
      className={`Title ${level} bold-${bold} ${className}`}
      style={{
        WebkitLineClamp: line === 1 ? 1 : undefined,
        display: line === 1 ? "-webkit-box" : undefined,
        WebkitBoxOrient: line === 1 ? "vertical" : undefined,
        overflow: line === 1 ? "hidden" : undefined,
        textOverflow: line === 1 ? "ellipsis" : undefined,
        whiteSpace: line === 1 ? "nowrap" : undefined,
      }}
      {...props}
    >
      {children}
    </Tag>
  );
};

// Définir les types des props
Title.propTypes = {
  children: PropTypes.node,
  level: PropTypes.oneOf(["h1", "h2", "h3", "h4", "h5", "h6"]),
  bold: PropTypes.oneOf([
    "thin",
    "extra-light",
    "light",
    "regular",
    "medium",
    "semi-bold",
    "bold",
    "extra-bold",
  ]),
  line: PropTypes.number,
  className: PropTypes.string,
};

export default Title;
